import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { GameScene } from './Scene';
import { PhysicsWorld } from './PhysicsWorld';
import { InputManager } from '../systems/InputManager';
import { Tank } from '../entities/Tank';
import { Enemy } from '../entities/Enemy';
import { createEnemy, EnemyType } from '../entities/EnemyTypes';
import { Bullet } from '../entities/Bullet';
import { MachineGunBullet } from '../entities/MachineGunBullet';
import { Missile } from '../entities/Missile';
import { Mine } from '../entities/Mine';
import { Powerup, PowerupType } from '../entities/Powerup';
import { DestructibleObstacle } from '../entities/DestructibleObstacle';
import { HUD } from '../ui/HUD';
import { Minimap } from '../ui/Minimap';
import { DamageText } from '../ui/DamageText';
import { ParticleSystem, ShieldEffect } from '../effects/ParticleSystem';
import { MapGenerator, MapData } from '../maps/MapGenerator';
import { WeaponManager } from '../weapons/WeaponManager';
import { AudioManager } from '../systems/AudioManager';
import { AudioControls } from '../ui/AudioControls';
import { PauseMenu } from '../ui/PauseMenu';
import { SaveManager } from '../systems/SaveManager';

export class Game {
  private scene: GameScene;
  private physics: PhysicsWorld;
  private input: InputManager;
  private hud: HUD;
  private minimap: Minimap;
  private damageText: DamageText;
  private particles: ParticleSystem;
  private audio: AudioManager;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private audioControls!: AudioControls;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private pauseMenu!: PauseMenu;
  private saveManager: SaveManager;

  private playerTank: Tank;
  private enemies: Enemy[] = [];
  private bullets: Bullet[] = [];
  private machineGunBullets: MachineGunBullet[] = [];
  private missiles: Missile[] = [];
  private mines: Mine[] = [];
  private powerups: Powerup[] = [];
  private destructibleObstacles: DestructibleObstacle[] = [];
  private playerShieldEffect: ShieldEffect | null = null;

  private weaponManager: WeaponManager;

  private isRunning: boolean = false;
  private lastTime: number = 0;

  private score: number = 0;
  private level: number = 1;
  private enemiesPerLevel: number = 8; // 基础敌人数量增加（适应更大的地图）

  // 地图系统
  private mapGenerator: MapGenerator;
  private currentMap: MapData | null = null;
  private mapTypes: Array<'city' | 'desert' | 'maze' | 'industrial'> = ['city', 'desert', 'maze', 'industrial'];

  // 障碍物位置数据（用于小地图）
  private obstacleData: Array<{ position: THREE.Vector3; size: { width: number; depth: number } }> = [];

  constructor(canvas: HTMLCanvasElement) {
    // 初始化系统
    this.scene = new GameScene(canvas);
    this.physics = new PhysicsWorld();
    this.input = new InputManager(canvas);
    this.hud = new HUD();
    this.minimap = new Minimap();
    this.damageText = new DamageText();
    this.particles = new ParticleSystem(this.scene.scene);

    // 初始化音频管理器
    this.audio = new AudioManager();
    this.audio.loadVolumeSettings();

    // 初始化存档管理器
    this.saveManager = new SaveManager();
    this.saveManager.startNewGame();

    // 初始化音频控制UI
    this.audioControls = new AudioControls(this.audio);
    void this.audioControls; // UI已自动显示

    // 初始化暂停菜单
    this.pauseMenu = new PauseMenu(this.audio);
    this.pauseMenu.onResume(() => {
      // 重置时间，避免暂停期间的 deltaTime 过大
      this.lastTime = performance.now();
    });
    this.pauseMenu.onRestart(() => {
      this.restart();
    });
    this.pauseMenu.onQuit(() => {
      // 刷新页面回到初始状态
      window.location.reload();
    });
    this.pauseMenu.onLoad((slot: number) => {
      return this.loadGame(slot);
    });
    void this.pauseMenu; // UI已自动显示

    // 初始化地图生成器
    this.mapGenerator = new MapGenerator(this.scene.scene, this.physics.world);

    // 初始化武器管理器
    this.weaponManager = new WeaponManager();

    // 创建玩家坦克
    this.playerTank = new Tank(0, 0, 0x00ff00);
    this.scene.add(this.playerTank.mesh);
    this.physics.addBody(this.playerTank.body);

    // 设置碰撞监听
    this.setupCollisionDetection();

    // 设置调试快捷键
    this.setupDebugKeys();

    // 初始化关卡（会加载地图）
    this.initLevel();
  }

  private loadMap(mapType: 'city' | 'desert' | 'maze' | 'industrial'): void {
    console.log(`Loading map: ${mapType}`);

    // 清除旧地图障碍物
    if (this.currentMap) {
      this.currentMap.obstacles.forEach(obs => {
        this.scene.remove(obs.mesh);
        this.physics.removeBody(obs.body);
      });
    }
    this.scene.clearObstacles();
    this.obstacleData = [];

    // 生成新地图
    switch (mapType) {
      case 'city':
        this.currentMap = this.mapGenerator.generateCityMap();
        this.scene.createGround(this.currentMap.size, 0x808080); // 灰色城市地面
        break;
      case 'desert':
        this.currentMap = this.mapGenerator.generateDesertMap();
        this.scene.createGround(this.currentMap.size, 0xC2B280); // 黄褐色沙漠
        break;
      case 'maze':
        this.currentMap = this.mapGenerator.generateMazeMap();
        this.scene.createGround(this.currentMap.size, 0x8B7355); // 棕色迷宫地面
        break;
      case 'industrial':
        this.currentMap = this.mapGenerator.generateIndustrialMap();
        this.scene.createGround(this.currentMap.size, 0x606060); // 深灰色工业地面
        break;
    }

    // 更新边界墙
    this.scene.createWalls(this.currentMap.size);

    // 更新小地图的世界大小
    this.minimap.setWorldSize(this.currentMap.size * 2);

    // 存储障碍物数据用于小地图
    this.obstacleData = this.currentMap.obstacles.map(obs => ({
      position: obs.position.clone(),
      size: obs.size
    }));

    console.log(`Map loaded: ${this.currentMap.name}, Size: ${this.currentMap.size}, Obstacles: ${this.currentMap.obstacles.length}`);
  }

  private setupCollisionDetection(): void {
    // console.log('Setting up collision detection...');

    // 使用 beginContact 事件
    this.physics.world.addEventListener('beginContact', (event: any) => {
      // console.log('beginContact event triggered!');
      const bodyA = event.bodyA;
      const bodyB = event.bodyB;

      this.handleCollision(bodyA, bodyB);
    });

    // 同时监听 collide 事件作为备份
    this.physics.world.addEventListener('collide', (event: any) => {
      // console.log('collide event triggered!');
      const bodyA = event.body;
      const bodyB = event.target;

      this.handleCollision(bodyA, bodyB);
    });

    console.log('Collision detection setup complete');
  }

  /**
   * 设置调试快捷键
   */
  private setupDebugKeys(): void {
    document.addEventListener('keydown', (e) => {
      // 按 F1 显示所有敌人位置
      if (e.key === 'F1') {
        console.log('=== ENEMY DEBUG INFO ===');
        console.log(`Total enemies: ${this.enemies.length}`);
        console.log(`Alive enemies: ${this.enemies.filter(e => e.isAlive).length}`);

        this.enemies.forEach((enemy, index) => {
          const pos = enemy.mesh.position;
          const distance = pos.distanceTo(this.playerTank.mesh.position);
          console.log(`Enemy ${index + 1}:`, {
            position: `(${pos.x.toFixed(1)}, ${pos.z.toFixed(1)})`,
            alive: enemy.isAlive,
            health: enemy.health,
            distance: distance.toFixed(1),
            aiState: enemy.getAIState()
          });
        });

        // 在小地图上高亮显示敌人
        this.highlightEnemiesOnMinimap();
      }

      // 按 F2 传送到最近的敌人
      if (e.key === 'F2') {
        const aliveEnemies = this.enemies.filter(e => e.isAlive);
        if (aliveEnemies.length > 0) {
          // 找最近的敌人
          let nearest = aliveEnemies[0];
          let minDist = this.playerTank.mesh.position.distanceTo(nearest.mesh.position);

          aliveEnemies.forEach(enemy => {
            const dist = this.playerTank.mesh.position.distanceTo(enemy.mesh.position);
            if (dist < minDist) {
              minDist = dist;
              nearest = enemy;
            }
          });

          // 传送到敌人附近
          const enemyPos = nearest.mesh.position;
          const offset = 15;
          this.playerTank.body.position.set(
            enemyPos.x + offset,
            0.5,
            enemyPos.z + offset
          );
          this.playerTank.mesh.position.set(
            enemyPos.x + offset,
            0.5,
            enemyPos.z + offset
          );

          console.log(`Teleported near enemy at (${enemyPos.x.toFixed(1)}, ${enemyPos.z.toFixed(1)})`);
        }
      }
    });
  }

  /**
   * 在小地图上高亮所有敌人
   */
  private highlightEnemiesOnMinimap(): void {
    // 这个方法会在下次 updateMinimap 调用时自动显示敌人
    // 敌人位置已经在 minimap 上了，只需要刷新即可
    console.log('Check minimap - red dots are enemy positions');
  }

  private handleCollision(bodyA: CANNON.Body, bodyB: CANNON.Body): void {
    const typeA = bodyA.userData?.type;
    const typeB = bodyB.userData?.type;

    // console.log(`Collision detected: ${typeA} <-> ${typeB}`);

    // 道具碰撞处理
    if (typeA === 'powerup' || typeB === 'powerup') {
      const powerup = typeA === 'powerup' ? bodyA.userData.powerup : bodyB.userData.powerup;
      const otherType = typeA === 'powerup' ? typeB : typeA;

      if (otherType === 'tank' && powerup && powerup.isActive) {
        // 玩家收集道具
        this.collectPowerup(powerup);

        // 播放道具收集音效
        this.audio.playSound('powerup_collect');
      }
    }

    // 子弹碰撞处理
    if (typeA === 'bullet' || typeB === 'bullet') {
      const bullet = typeA === 'bullet' ? bodyA.userData.bullet : bodyB.userData.bullet;
      const other = typeA === 'bullet' ? bodyB : bodyA;
      const otherType = typeA === 'bullet' ? typeB : typeA;

      if (!bullet || !bullet.isActive) return;

      // 子弹击中坦克
      if (otherType === 'tank' && bullet.owner === 'enemy') {
        // 敌人子弹击中玩家
        const damage = other.userData.tank?.getDamage ? other.userData.tank.getDamage() : 20;
        const actualDamage = this.playerTank.hasShield ? damage * 0.5 : damage;
        this.playerTank.takeDamage(damage);
        bullet.destroy();
        this.particles.createExplosion(bullet.mesh.position);

        // 播放击中和爆炸音效
        this.audio.playSound('tank_hit');
        this.audio.playSound('explosion_small', 0.8);

        // 显示伤害数字
        this.damageText.showDamage(
          this.playerTank.mesh.position,
          actualDamage,
          this.scene.camera,
          false
        );

        // 玩家受击闪烁
        this.flashTank(this.playerTank, 0xff0000);

        if (!this.playerTank.isAlive) {
          this.gameOver(false);
        }
      } else if ((otherType === 'enemy' || otherType === 'heavy_enemy' || otherType === 'light_enemy' || otherType === 'sniper_enemy') && bullet.owner === 'player') {
        // 玩家子弹击中敌人
        console.log('Player bullet hit enemy!');
        const enemy = other.userData.tank;
        if (enemy && enemy.isAlive) {
          const damage = 34; // 3 发子弹击毁普通敌人
          const isCritical = Math.random() < 0.15; // 15% 暴击率
          const finalDamage = isCritical ? damage * 1.5 : damage;

          console.log(`Dealing ${finalDamage} damage (critical: ${isCritical})`);

          enemy.takeDamage(finalDamage);
          bullet.destroy();
          this.particles.createExplosion(bullet.mesh.position);

          // 播放击中音效
          this.audio.playSound('tank_hit');
          this.audio.playSound('explosion_small', 0.6);

          // 显示伤害数字
          console.log('About to show damage text...');
          this.damageText.showDamage(
            enemy.mesh.position,
            finalDamage,
            this.scene.camera,
            isCritical
          );
          console.log('Damage text shown');

          // 敌人受击闪烁
          this.flashTank(enemy, 0xffffff);

          if (!enemy.isAlive) {
            const score = enemy.getScore ? enemy.getScore() : 100;
            this.score += score;
            this.particles.createExplosion(enemy.mesh.position, 2);

            // 播放坦克摧毁音效
            this.audio.playSound('tank_destroyed');
            this.audio.playSound('explosion_large');

            // 更新击杀统计
            this.saveManager.addKill();

            // 显示击毁提示
            this.damageText.showDamage(
              enemy.mesh.position,
              score,
              this.scene.camera,
              true
            );

            this.checkLevelComplete();
          }
        }
      } else if (otherType === 'wall' || otherType === 'obstacle' || otherType === 'destructible_obstacle') {
        // 子弹击中墙或障碍物
        bullet.destroy();
        this.particles.createExplosion(bullet.mesh.position, 0.5);
      }
    }

    // 机枪子弹碰撞处理
    if (typeA === 'machinegun_bullet' || typeB === 'machinegun_bullet') {
      const bullet = typeA === 'machinegun_bullet' ? bodyA.userData.bullet : bodyB.userData.bullet;
      const other = typeA === 'machinegun_bullet' ? bodyB : bodyA;
      const otherType = typeA === 'machinegun_bullet' ? typeB : typeA;

      if (!bullet || !bullet.isActive) return;

      // 击中玩家坦克
      if (otherType === 'tank' && bullet.owner === 'enemy') {
        const actualDamage = this.playerTank.hasShield ? bullet.damage * 0.5 : bullet.damage;
        this.playerTank.takeDamage(bullet.damage);
        bullet.destroy();
        this.particles.createExplosion(bullet.mesh.position, 0.3);
        this.damageText.showDamage(this.playerTank.mesh.position, actualDamage, this.scene.camera, false);
        this.flashTank(this.playerTank, 0xff0000);
        if (!this.playerTank.isAlive) this.gameOver(false);
      } else if ((otherType === 'enemy' || otherType === 'heavy_enemy' || otherType === 'light_enemy' || otherType === 'sniper_enemy') && bullet.owner === 'player') {
        // 击中敌人
        const enemy = other.userData.tank;
        if (enemy && enemy.isAlive) {
          enemy.takeDamage(bullet.damage);
          bullet.destroy();
          this.particles.createExplosion(bullet.mesh.position, 0.3);
          this.damageText.showDamage(enemy.mesh.position, bullet.damage, this.scene.camera, false);
          this.flashTank(enemy, 0xffffff);
          if (!enemy.isAlive) {
            this.score += enemy.getScore ? enemy.getScore() : 100;
            this.particles.createExplosion(enemy.mesh.position, 2);
            this.checkLevelComplete();
          }
        }
      } else if (otherType === 'wall' || otherType === 'obstacle' || otherType === 'destructible_obstacle') {
        bullet.destroy();
        this.particles.createExplosion(bullet.mesh.position, 0.2);
      }
    }

    // 导弹碰撞处理
    if (typeA === 'missile' || typeB === 'missile') {
      const missile = typeA === 'missile' ? bodyA.userData.missile : bodyB.userData.missile;

      if (!missile || !missile.isActive || missile.hasExploded) return;

      missile.explode();
      const explosionPos = missile.getExplosionPosition();
      this.particles.createExplosion(explosionPos, 3);

      // AOE伤害
      const aoeRadius = missile.areaOfEffect;
      const damage = missile.damage;

      // 对玩家造成AOE伤害
      if (missile.owner === 'enemy') {
        const distToPlayer = this.playerTank.mesh.position.distanceTo(explosionPos);
        if (distToPlayer <= aoeRadius) {
          const damageMultiplier = 1 - (distToPlayer / aoeRadius);
          const actualDamage = damage * damageMultiplier;
          this.playerTank.takeDamage(actualDamage);
          this.damageText.showDamage(this.playerTank.mesh.position, actualDamage, this.scene.camera, false);
          this.flashTank(this.playerTank, 0xff0000);
          if (!this.playerTank.isAlive) this.gameOver(false);
        }
      }

      // 对敌人造成AOE伤害
      if (missile.owner === 'player') {
        this.enemies.forEach(enemy => {
          if (!enemy.isAlive) return;
          const distToEnemy = enemy.mesh.position.distanceTo(explosionPos);
          if (distToEnemy <= aoeRadius) {
            const damageMultiplier = 1 - (distToEnemy / aoeRadius);
            const actualDamage = damage * damageMultiplier;
            enemy.takeDamage(actualDamage);
            this.damageText.showDamage(enemy.mesh.position, actualDamage, this.scene.camera, true);
            this.flashTank(enemy, 0xffffff);
            if (!enemy.isAlive) {
              this.score += enemy.getScore ? enemy.getScore() : 100;
              this.particles.createExplosion(enemy.mesh.position, 2);
              this.checkLevelComplete();
            }
          }
        });
      }
    }

    // 可摧毁障碍物碰撞处理
    if (typeA === 'destructible_obstacle' || typeB === 'destructible_obstacle') {
      const obstacle = typeA === 'destructible_obstacle' ? bodyA.userData.obstacle : bodyB.userData.obstacle;
      const other = typeA === 'destructible_obstacle' ? bodyB : bodyA;
      const otherType = typeA === 'destructible_obstacle' ? typeB : typeA;

      if (!obstacle || obstacle.isDestroyed) return;

      // 子弹击中障碍物
      if (otherType === 'bullet' || otherType === 'machinegun_bullet') {
        const bullet = other.userData.bullet;
        if (bullet && bullet.isActive) {
          const damage = otherType === 'bullet' ? 34 : (bullet.damage || 8);
          obstacle.takeDamage(damage);

          if (obstacle.isDestroyed) {
            this.particles.createExplosion(obstacle.mesh.position, 2);
            this.score += 50;
          }
        }
      }
    }
  }

  private collectPowerup(powerup: Powerup): void {
    const effect = powerup.getEffect();

    switch (effect.type) {
      case 'health':
        this.playerTank.heal(effect.value);
        this.particles.createPowerupEffect(powerup.mesh.position, 0x00ff00);
        break;
      case 'shield':
        this.playerTank.activateShield(effect.value);
        if (!this.playerShieldEffect) {
          this.playerShieldEffect = this.particles.createShieldEffect(this.playerTank.mesh.position);
        }
        this.particles.createPowerupEffect(powerup.mesh.position, 0x00ffff);
        break;
      case 'ammo':
        // 减少射击冷却时间
        (this.playerTank as any).shootCooldown = Math.max(0.2, (this.playerTank as any).shootCooldown - 0.05);
        this.particles.createPowerupEffect(powerup.mesh.position, 0xffa500);
        break;
    }

    powerup.collect();
  }

  private initLevel(): void {
    // 选择地图（每个关卡轮换不同地图）
    const mapType = this.mapTypes[(this.level - 1) % this.mapTypes.length];
    this.loadMap(mapType);

    // 清除现有敌人
    this.enemies.forEach(enemy => {
      this.scene.remove(enemy.mesh);
      this.physics.removeBody(enemy.body);
    });
    this.enemies = [];

    // 清除现有道具
    this.powerups.forEach(powerup => {
      this.scene.remove(powerup.mesh);
      this.physics.removeBody(powerup.body);
    });
    this.powerups = [];

    // 重置玩家位置到地图的出生点
    if (this.currentMap && this.currentMap.spawnPoints.length > 0) {
      const spawnPoint = this.currentMap.spawnPoints[0];
      this.playerTank.reset(spawnPoint.x, spawnPoint.z);
    }

    // 生成新敌人（使用地图的敌人出生点）
    // 每关增加2个敌人，适应更大的地图
    const numEnemies = this.enemiesPerLevel + (this.level - 1) * 2;
    if (this.currentMap && this.currentMap.enemySpawnPoints.length > 0) {
      for (let i = 0; i < numEnemies; i++) {
        // 使用地图的敌人出生点（循环使用）
        const spawnPoint = this.currentMap.enemySpawnPoints[i % this.currentMap.enemySpawnPoints.length];

        // 添加一些随机偏移，避免敌人重叠
        const offsetX = (Math.random() - 0.5) * 5;
        const offsetZ = (Math.random() - 0.5) * 5;
        const x = spawnPoint.x + offsetX;
        const z = spawnPoint.z + offsetZ;

        // 根据关卡和随机选择敌人类型
        let enemyType: EnemyType = 'normal';
        const rand = Math.random();

        if (this.level >= 2) {
          if (rand < 0.3) enemyType = 'light';
          else if (rand < 0.6) enemyType = 'heavy';
          else if (rand < 0.8) enemyType = 'sniper';
        }

        const enemy = createEnemy(enemyType, x, z);
        this.enemies.push(enemy);
        this.scene.add(enemy.mesh);
        this.physics.addBody(enemy.body);
      }
    }

    // 为所有敌人设置障碍物信息（用于AI避障）
    this.updateEnemyObstacles();

    // 生成道具
    this.spawnPowerups();

    // 更新 HUD
    this.hud.updateLevel(this.level);
    this.updateHUD();
  }

  /**
   * 更新敌人的障碍物信息（用于AI避障）
   */
  private updateEnemyObstacles(): void {
    if (!this.currentMap) return;

    // 收集障碍物信息
    const obstacles: Array<{ position: THREE.Vector3; radius: number }> = [];

    // 添加地图障碍物
    this.currentMap.obstacles.forEach(obs => {
      obstacles.push({
        position: obs.position.clone(),
        radius: Math.max(obs.size.width, obs.size.depth) / 2 + 2 // 半径加一点缓冲
      });
    });

    // 添加墙壁作为障碍物
    const mapSize = this.currentMap.size;

    // 四面墙（简化为四个大障碍物）
    obstacles.push(
      { position: new THREE.Vector3(0, 0, mapSize), radius: mapSize }, // 上墙
      { position: new THREE.Vector3(0, 0, -mapSize), radius: mapSize }, // 下墙
      { position: new THREE.Vector3(mapSize, 0, 0), radius: mapSize }, // 右墙
      { position: new THREE.Vector3(-mapSize, 0, 0), radius: mapSize }  // 左墙
    );

    // 为每个敌人设置障碍物信息
    this.enemies.forEach(enemy => {
      enemy.setObstacles(obstacles);
    });

    console.log(`Updated AI obstacles for ${this.enemies.length} enemies: ${obstacles.length} obstacles`);
  }

  private spawnPowerups(): void {
    const powerupCount = 2 + this.level;
    const powerupTypes: PowerupType[] = ['health', 'shield', 'ammo'];

    for (let i = 0; i < powerupCount; i++) {
      // 随机位置（避免太靠近中心）
      const angle = Math.random() * Math.PI * 2;
      const distance = 15 + Math.random() * 25;
      const x = Math.cos(angle) * distance;
      const z = Math.sin(angle) * distance;

      // 随机类型
      const type = powerupTypes[Math.floor(Math.random() * powerupTypes.length)];

      const powerup = new Powerup(x, z, type);
      this.powerups.push(powerup);
      this.scene.add(powerup.mesh);
      this.physics.addBody(powerup.body);
    }
  }

  private checkLevelComplete(): void {
    const aliveEnemies = this.enemies.filter(e => e.isAlive).length;
    if (aliveEnemies === 0) {
      this.level++;
      this.score += 500; // 关卡奖励

      // 自动保存游戏进度
      this.autoSave();

      setTimeout(() => {
        this.initLevel();
      }, 1000);
    }
  }

  /**
   * 自动保存游戏（完成关卡时）
   * 保存的是下一关开始的状态（满血、默认武器、满弹药）
   */
  private autoSave(): void {
    const weaponAmmo: { [key: string]: number } = {
      cannon: 50,
      machinegun: 200,
      missile: 10,
      mine: 5
    };

    // 保存满血状态，以便加载后从新关卡开始
    const success = this.saveManager.autoSave(
      this.level,
      this.score,
      100, // 满血
      'cannon', // 默认武器
      weaponAmmo
    );

    if (success) {
      console.log('Game auto-saved: Level', this.level, 'Score', this.score);
    }
  }

  /**
   * 手动保存游戏到指定槽位
   */
  saveGame(slot: number): boolean {
    const weaponAmmo: { [key: string]: number } = {};
    const weapons = ['cannon', 'machinegun', 'missile', 'mine'];
    weapons.forEach(type => {
      const weapon = this.weaponManager.getWeapon(type as any);
      if (weapon) {
        weaponAmmo[type] = weapon.currentAmmo;
      }
    });

    console.log('=== SAVING GAME ===');
    console.log('Slot:', slot);
    console.log('Level:', this.level);
    console.log('Score:', this.score);
    console.log('Health:', this.playerTank.health);
    console.log('Current Weapon:', this.weaponManager.getCurrentWeaponType());
    console.log('Weapon Ammo:', weaponAmmo);

    return this.saveManager.save(
      slot,
      this.level,
      this.score,
      this.playerTank.health,
      this.weaponManager.getCurrentWeaponType(),
      weaponAmmo
    );
  }

  /**
   * 从指定槽位加载游戏
   */
  loadGame(slot: number): boolean {
    const saveData = this.saveManager.load(slot);
    if (!saveData) {
      console.log('No save data found in slot', slot);
      return false;
    }

    console.log('=== LOADING GAME ===');
    console.log('Save Data:', saveData);
    console.log('Before load - Level:', this.level, 'Score:', this.score, 'Health:', this.playerTank.health);

    // 恢复基础游戏状态
    this.level = saveData.level;
    this.score = saveData.score;

    console.log('After setting level/score - Level:', this.level, 'Score:', this.score);

    // 清除粒子和伤害数字
    this.particles.clear();
    this.damageText.clear();

    // 重新初始化关卡（会重置玩家生命值和位置）
    console.log('Before initLevel - Level:', this.level);
    this.initLevel();
    console.log('After initLevel - Level:', this.level, 'Health:', this.playerTank.health);

    // 在 initLevel 之后恢复玩家生命值（因为 initLevel 会调用 reset 重置生命值）
    this.playerTank.health = saveData.playerHealth;
    console.log('After restoring health - Health:', this.playerTank.health);

    // 恢复武器状态
    this.weaponManager.switchWeapon(saveData.currentWeapon as any);
    Object.keys(saveData.weaponAmmo).forEach(type => {
      const weapon = this.weaponManager.getWeapon(type as any);
      if (weapon) {
        weapon.currentAmmo = saveData.weaponAmmo[type];
        console.log(`Restored ${type} ammo to ${weapon.currentAmmo}`);
      }
    });

    // 隐藏游戏结束界面
    const overlay = document.getElementById('game-overlay');
    if (overlay) {
      overlay.classList.add('hidden');
    }

    // 重启游戏循环
    if (!this.isRunning) {
      this.start();
    }

    console.log('=== LOAD COMPLETE ===');
    console.log('Final - Level:', this.level, 'Score:', this.score, 'Health:', this.playerTank.health);
    return true;
  }

  /**
   * 获取存档管理器（用于UI访问）
   */
  getSaveManager(): SaveManager {
    return this.saveManager;
  }

  start(): void {
    if (this.isRunning) return;
    this.isRunning = true;
    this.lastTime = performance.now();

    // 播放战斗背景音乐
    this.audio.playMusic('battle');

    this.gameLoop();
  }

  restart(): void {
    // 重置玩家
    this.playerTank.reset(0, 0);

    // 重置游戏状态
    this.score = 0;
    this.level = 1;

    // 清除子弹
    this.bullets.forEach(bullet => {
      this.scene.remove(bullet.mesh);
      this.physics.removeBody(bullet.body);
    });
    this.bullets = [];

    // 清除粒子
    this.particles.clear();

    // 清除伤害数字
    this.damageText.clear();

    // 移除护盾特效
    if (this.playerShieldEffect) {
      this.playerShieldEffect.remove(this.scene.scene);
      this.playerShieldEffect = null;
    }

    // 重新初始化关卡
    this.initLevel();

    // 隐藏游戏结束界面
    const overlay = document.getElementById('game-overlay')!;
    overlay.classList.add('hidden');

    this.start();
  }

  private gameLoop = (): void => {
    if (!this.isRunning) return;

    const currentTime = performance.now();
    const deltaTime = Math.min((currentTime - this.lastTime) / 1000, 0.1);
    this.lastTime = currentTime;

    this.update(deltaTime);
    this.render();

    requestAnimationFrame(this.gameLoop);
  };

  private update(deltaTime: number): void {
    // 检查暂停状态
    if (this.pauseMenu.isPausedState()) {
      return; // 游戏暂停时不更新
    }

    // 更新物理世界
    this.physics.update(deltaTime);

    // 更新场景（云朵动画等）
    this.scene.update(deltaTime);

    // 更新武器管理器
    this.weaponManager.update(deltaTime);

    // 更新玩家控制
    this.updatePlayerControl(deltaTime);

    // 更新玩家坦克
    this.playerTank.update(deltaTime);

    // 更新敌人
    this.enemies.forEach(enemy => {
      if (enemy.isAlive) {
        enemy.updateAI(this.playerTank.mesh.position, deltaTime);
        enemy.update(deltaTime);

        // 敌人射击
        if (enemy.shouldShoot()) {
          this.createProjectile(enemy, 'enemy');
        }
      }
    });

    // 更新子弹（并添加烟雾轨迹）
    for (let i = this.bullets.length - 1; i >= 0; i--) {
      const bullet = this.bullets[i];
      bullet.update(deltaTime);

      // 添加烟雾轨迹
      if (Math.random() < 0.3) {
        this.particles.createSmokeTrail(bullet.mesh.position);
      }

      if (!bullet.isActive) {
        this.scene.remove(bullet.mesh);
        this.physics.removeBody(bullet.body);
        this.bullets.splice(i, 1);
      }
    }

    // 更新道具
    for (let i = this.powerups.length - 1; i >= 0; i--) {
      const powerup = this.powerups[i];
      powerup.update(deltaTime);

      if (!powerup.isActive) {
        this.scene.remove(powerup.mesh);
        this.physics.removeBody(powerup.body);
        this.powerups.splice(i, 1);
      }
    }

    // 更新机枪子弹
    for (let i = this.machineGunBullets.length - 1; i >= 0; i--) {
      const bullet = this.machineGunBullets[i];
      bullet.update(deltaTime);

      if (!bullet.isActive) {
        this.scene.remove(bullet.mesh);
        this.physics.removeBody(bullet.body);
        this.machineGunBullets.splice(i, 1);
      }
    }

    // 更新导弹
    for (let i = this.missiles.length - 1; i >= 0; i--) {
      const missile = this.missiles[i];
      missile.update(deltaTime);

      // 添加尾焰效果
      if (Math.random() < 0.5) {
        this.particles.createSmokeTrail(missile.mesh.position);
      }

      if (!missile.isActive) {
        this.scene.remove(missile.mesh);
        this.physics.removeBody(missile.body);
        this.missiles.splice(i, 1);
      }
    }

    // 更新地雷并检查触发
    for (let i = this.mines.length - 1; i >= 0; i--) {
      const mine = this.mines[i];
      mine.update(deltaTime);

      // 检查玩家触发
      if (mine.owner === 'enemy' && mine.isArmed && !mine.hasExploded) {
        if (mine.checkTrigger(this.playerTank.mesh.position, 'tank')) {
          mine.explode();
          const explosionPos = mine.getExplosionPosition();
          this.particles.createExplosion(explosionPos, 3);

          const distToPlayer = this.playerTank.mesh.position.distanceTo(explosionPos);
          if (distToPlayer <= mine.areaOfEffect) {
            const damageMultiplier = 1 - (distToPlayer / mine.areaOfEffect);
            const actualDamage = mine.damage * damageMultiplier;
            this.playerTank.takeDamage(actualDamage);
            this.damageText.showDamage(this.playerTank.mesh.position, actualDamage, this.scene.camera, false);
            this.flashTank(this.playerTank, 0xff0000);
            if (!this.playerTank.isAlive) this.gameOver(false);
          }
        }
      }

      // 检查敌人触发
      if (mine.owner === 'player' && mine.isArmed && !mine.hasExploded) {
        this.enemies.forEach(enemy => {
          if (!enemy.isAlive) return;
          const enemyType = enemy.body.userData.type || 'enemy';
          if (mine.checkTrigger(enemy.mesh.position, enemyType)) {
            mine.explode();
            const explosionPos = mine.getExplosionPosition();
            this.particles.createExplosion(explosionPos, 3);

            this.enemies.forEach(e => {
              if (!e.isAlive) return;
              const distToEnemy = e.mesh.position.distanceTo(explosionPos);
              if (distToEnemy <= mine.areaOfEffect) {
                const damageMultiplier = 1 - (distToEnemy / mine.areaOfEffect);
                const actualDamage = mine.damage * damageMultiplier;
                e.takeDamage(actualDamage);
                this.damageText.showDamage(e.mesh.position, actualDamage, this.scene.camera, true);
                this.flashTank(e, 0xffffff);
                if (!e.isAlive) {
                  this.score += e.getScore ? e.getScore() : 100;
                  this.particles.createExplosion(e.mesh.position, 2);
                  this.checkLevelComplete();
                }
              }
            });
          }
        });
      }

      if (!mine.isActive) {
        this.scene.remove(mine.mesh);
        this.physics.removeBody(mine.body);
        this.mines.splice(i, 1);
      }
    }

    // 更新可摧毁障碍物
    for (let i = this.destructibleObstacles.length - 1; i >= 0; i--) {
      const obstacle = this.destructibleObstacles[i];
      obstacle.update(deltaTime);

      if (obstacle.isDestroyed) {
        this.scene.remove(obstacle.mesh);
        this.physics.removeBody(obstacle.body);
        this.destructibleObstacles.splice(i, 1);
      }
    }

    // 更新粒子系统
    this.particles.update(deltaTime);

    // 更新伤害数字
    this.damageText.update(deltaTime, this.scene.camera);

    // 更新护盾特效
    if (this.playerTank.hasShield && this.playerShieldEffect) {
      this.playerShieldEffect.update(deltaTime, this.playerTank.mesh.position);
    } else if (!this.playerTank.hasShield && this.playerShieldEffect) {
      this.playerShieldEffect.remove(this.scene.scene);
      this.playerShieldEffect = null;
    }

    // 更新相机
    this.scene.updateCamera(
      this.playerTank.mesh.position,
      this.playerTank.getRotation()
    );

    // 更新小地图
    this.updateMinimap();

    // 更新 HUD
    this.updateHUD();
  }

  private updateMinimap(): void {
    this.minimap.clear();

    // 使用当前地图的大小绘制边界墙
    const arenaSize = this.currentMap ? this.currentMap.size : 50;
    this.minimap.drawWall(new THREE.Vector3(0, 0, arenaSize), { width: arenaSize * 2, depth: 1 });
    this.minimap.drawWall(new THREE.Vector3(0, 0, -arenaSize), { width: arenaSize * 2, depth: 1 });
    this.minimap.drawWall(new THREE.Vector3(arenaSize, 0, 0), { width: 1, depth: arenaSize * 2 });
    this.minimap.drawWall(new THREE.Vector3(-arenaSize, 0, 0), { width: 1, depth: arenaSize * 2 });

    // 绘制障碍物
    this.obstacleData.forEach(obstacle => {
      this.minimap.drawObstacle(obstacle.position, obstacle.size);
    });

    // 绘制敌人
    this.enemies.forEach(enemy => {
      this.minimap.drawEnemy(enemy.mesh.position, enemy.isAlive);
    });

    // 绘制道具
    this.powerups.forEach(powerup => {
      this.minimap.drawPowerup(powerup.mesh.position, powerup.type);
    });

    // 绘制玩家（最后绘制，确保在最上层）
    this.minimap.drawPlayer(this.playerTank.mesh.position, this.playerTank.getRotation());
  }

  private updatePlayerControl(deltaTime: number): void {
    if (!this.playerTank.isAlive) return;

    // 移动控制
    let forward = 0;
    let turn = 0;

    if (this.input.isKeyPressed('w')) forward = 1;
    if (this.input.isKeyPressed('s')) forward = -1;
    if (this.input.isKeyPressed('a')) turn = 1;
    if (this.input.isKeyPressed('d')) turn = -1;

    this.playerTank.move(forward, turn, deltaTime);

    // 炮塔控制 - 跟随鼠标
    const mousePos = this.input.getMousePosition();
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(new THREE.Vector2(mousePos.x, mousePos.y), this.scene.camera);

    // 创建一个地面平面用于射线检测
    const groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const intersectPoint = new THREE.Vector3();
    raycaster.ray.intersectPlane(groundPlane, intersectPoint);

    if (intersectPoint) {
      const direction = new THREE.Vector3()
        .subVectors(intersectPoint, this.playerTank.mesh.position)
        .normalize();

      const angle = Math.atan2(direction.x, direction.z);
      const bodyAngle = this.playerTank.getRotation();
      this.playerTank.rotateTurret(angle - bodyAngle);
    }

    // 武器切换控制 (数字键1-4)
    if (this.input.isKeyPressed('1')) {
      if (this.weaponManager.getCurrentWeapon().config.type !== 'cannon') {
        this.weaponManager.switchWeapon('cannon');
        this.audio.playSound('weapon_switch');
      }
    }
    if (this.input.isKeyPressed('2')) {
      if (this.weaponManager.getCurrentWeapon().config.type !== 'machinegun') {
        this.weaponManager.switchWeapon('machinegun');
        this.audio.playSound('weapon_switch');
      }
    }
    if (this.input.isKeyPressed('3')) {
      if (this.weaponManager.getCurrentWeapon().config.type !== 'missile') {
        this.weaponManager.switchWeapon('missile');
        this.audio.playSound('weapon_switch');
      }
    }
    if (this.input.isKeyPressed('4')) {
      if (this.weaponManager.getCurrentWeapon().config.type !== 'mine') {
        this.weaponManager.switchWeapon('mine');
        this.audio.playSound('weapon_switch');
      }
    }

    // 射击控制
    if (this.input.isKeyPressed(' ')) {
      this.createProjectile(this.playerTank, 'player');
    }
  }

  private createProjectile(tank: Tank, owner: 'player' | 'enemy'): void {
    if (!tank.canShoot || !tank.isAlive) return;

    const currentWeapon = owner === 'player' ? this.weaponManager.getCurrentWeapon() : null;

    // 敌人只使用默认主炮
    if (owner === 'enemy' || !currentWeapon || currentWeapon.config.type === 'cannon') {
      const canFire = currentWeapon ? currentWeapon.tryFire() : true;
      if (!canFire) return;

      tank.shoot();
      const position = tank.getBarrelEnd();
      const direction = tank.getBarrelDirection();
      const bullet = new Bullet(position, direction, owner);
      this.bullets.push(bullet);
      this.scene.add(bullet.mesh);
      this.physics.addBody(bullet.body);
      this.particles.createMuzzleFlash(position, direction);

      // 播放炮击音效
      this.audio.playSound('cannon_fire');

      return;
    }

    // 玩家武器系统
    if (!currentWeapon.tryFire()) return;
    tank.shoot();

    const position = tank.getBarrelEnd();
    const direction = tank.getBarrelDirection();

    switch (currentWeapon.config.type) {
      case 'machinegun':
        const mgBullet = currentWeapon.createProjectile(position, direction, owner);
        this.machineGunBullets.push(mgBullet);
        this.scene.add(mgBullet.mesh);
        this.physics.addBody(mgBullet.body);
        this.particles.createMuzzleFlash(position, direction);

        // 播放机枪音效
        this.audio.playSound('machinegun_fire');
        break;

      case 'missile':
        // 找最近的敌人作为目标
        let nearestEnemy: THREE.Vector3 | undefined;
        let minDist = Infinity;
        this.enemies.forEach(enemy => {
          if (!enemy.isAlive) return;
          const dist = enemy.mesh.position.distanceTo(position);
          if (dist < minDist) {
            minDist = dist;
            nearestEnemy = enemy.mesh.position;
          }
        });

        const missile = (currentWeapon as any).createProjectileWithTarget(position, direction, owner, nearestEnemy);
        this.missiles.push(missile);
        this.scene.add(missile.mesh);
        this.physics.addBody(missile.body);
        this.particles.createMuzzleFlash(position, direction);

        // 播放导弹发射音效
        this.audio.playSound('missile_launch');
        break;

      case 'mine':
        const mine = currentWeapon.createProjectile(position, direction, owner);
        this.mines.push(mine);
        this.scene.add(mine.mesh);
        this.physics.addBody(mine.body);

        // 播放地雷放置音效
        this.audio.playSound('mine_place');
        break;
    }
  }

  private updateHUD(): void {
    this.hud.updateHealth(this.playerTank.health, this.playerTank.maxHealth);
    this.hud.updateScore(this.score);
    this.hud.updateEnemies(this.enemies.filter(e => e.isAlive).length);
    this.hud.updateShield(this.playerTank.hasShield, this.playerTank.shieldTime);

    // 更新武器信息
    const currentWeapon = this.weaponManager.getCurrentWeapon();
    this.hud.updateWeapon(
      this.weaponManager.getCurrentWeaponType(),
      currentWeapon.currentAmmo,
      currentWeapon.config.maxAmmo
    );
  }

  private gameOver(won: boolean): void {
    this.isRunning = false;
    this.hud.showGameOver(won, this.score);

    // 更新死亡统计
    if (!won) {
      this.saveManager.addDeath();
    }

    // 停止战斗音乐，播放胜利/失败音乐
    this.audio.stopMusic();
    if (won) {
      this.audio.playMusic('victory');
    } else {
      this.audio.playMusic('gameover');
    }
  }

  // 坦克受击闪烁效果
  private flashTank(tank: Tank, color: number): void {
    const originalMaterials: THREE.MeshStandardMaterial[] = [];

    // 保存原始材质
    tank.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh && child.material instanceof THREE.MeshStandardMaterial) {
        originalMaterials.push(child.material.clone());
        // 设置闪烁颜色
        child.material.emissive.setHex(color);
        child.material.emissiveIntensity = 0.5;
      }
    });

    // 200ms 后恢复
    setTimeout(() => {
      let index = 0;
      tank.mesh.traverse((child) => {
        if (child instanceof THREE.Mesh && child.material instanceof THREE.MeshStandardMaterial) {
          child.material.emissive.setHex(originalMaterials[index].emissive.getHex());
          child.material.emissiveIntensity = originalMaterials[index].emissiveIntensity;
          index++;
        }
      });
    }, 200);
  }

  private render(): void {
    this.scene.render();
  }
}
